
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { sendEmail } from "@/utils/email";
import { AlertCircle, CheckCircle2, Save } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { EmailTemplateType } from "@/utils/email-templates";
import { generateTemplateGroups } from "@/utils/email-templates/templateCategories";
import { sendNotification } from "@/utils/sendNotification";

interface EmailTestSectionProps {
  fromAddress: string;
  onFromAddressChange?: (address: string) => void;
}

export const EmailTestSection = ({
  fromAddress,
  onFromAddressChange,
}: EmailTestSectionProps) => {
  const [testEmail, setTestEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [localFromAddress, setLocalFromAddress] = useState(fromAddress);
  const [selectedTemplate, setSelectedTemplate] = useState<
    EmailTemplateType | "custom"
  >("authSignup");
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message?: string;
  } | null>(null);

  const templateGroups = generateTemplateGroups();

  const handleTestEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testEmail) return;

    setIsSending(true);
    setTestResult(null);

    try {
      let result;

      console.log("Test email - selected template:", selectedTemplate);

      if (selectedTemplate === "custom") {
        console.log("Sending custom test email");
        result = await supabase.functions.invoke("send-email", {
          body: {
            to: testEmail,
            subject: "NationCard Test Email",
            html: `
              <div style="background: #fff; font-family: 'Segoe UI','Helvetica Neue',Arial,sans-serif; padding: 0; margin: 0;">
                <div style="text-align: center; padding: 32px 0 12px 0;">
                  <img src="https://nationcard.org/img/nationcard-light.png" alt="NationCard™ Logo" width="160" height="60" style="display:block; margin:0 auto 20px auto; max-width:100%; height:auto;" />
                </div>
                <div style="max-width: 500px; margin: 0 auto; background: #fff; border-radius: 12px; box-shadow:0 2px 12px #eee; padding: 36px 36px 26px 36px;">
                  <h1 style="font-size: 24px; color: #1A1F2C; margin-bottom: 20px; margin-top:0;">Test Email</h1>
                  <p style="font-size: 16px; color: #262736;">This is a test email from NationCard to verify that email sending is working properly.</p>
                  <p style="font-size: 16px; color: #262736;">If you received this email, then the email delivery system is functioning correctly.</p>
                  <p style="font-size: 14px; color: #8E9196;">Time sent: ${new Date().toLocaleString()}</p>
                  <div style="color: #8E9196; font-size: 13px; padding-top: 18px;">
                    <hr style="border: none; border-top: 1px solid #eee; margin: 18px 0 19px 0;" />
                    <p style="margin: 0;">Best regards,<br>The NationCard™ Team</p>
                  </div>
                </div>
              </div>
            `,
            text: "This is a test email from NationCard to verify that email sending is working properly.",
            email_type: "custom",
          },
        });
      } else {
        console.log("Sending template email with type:", selectedTemplate);
        result = await sendNotification(
          selectedTemplate as EmailTemplateType,
          testEmail,
          {
            recipientName: "Test User",
            credentials: { email: testEmail, password: "test-password" },
            countryName: "Cayman Islands",
            formattedLaunchDate: new Date().toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            }),
            pollData: {
              title: "Sample Poll",
              description: "This is a sample poll description for testing",
            },
            insightData: {
              title: "Sample Insight",
              description: "This is a sample insight description for testing",
            },
            notes: "This is a test email sent from the admin panel.",
            confirmationUrl:
              "https://nationcard.org/profile?tab=privacy&delete_confirm=test-token",
            coolingOffDays: 7,
            coolingOffUntil: new Date(
              Date.now() + 7 * 24 * 60 * 60 * 1000
            ).toISOString(),
          }
        );
      }

      console.log("Test email result:", result);

      setTestResult({
        success: true,
        message: "Test email sent successfully! Please check your inbox.",
      });

      toast.success("Test email sent successfully");
    } catch (error: any) {
      console.error("Test email error:", error);

      setTestResult({
        success: false,
        message: `Error sending test email: ${error.message || "Unknown error"}`,
      });

      toast.error("Failed to send test email");
    } finally {
      setIsSending(false);
    }
  };

  const handleFromAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFromAddress = e.target.value;
    setLocalFromAddress(newFromAddress);
  };

  const handleSaveFromAddress = async () => {
    if (!localFromAddress) return;

    setIsSaving(true);
    try {
      const { error } = await supabase.from("app_settings").upsert(
        {
          key: "email_from_address",
          value: localFromAddress,
        },
        {
          onConflict: "key",
        }
      );

      if (error) throw error;

      onFromAddressChange?.(localFromAddress);
      toast.success("From address saved successfully");
    } catch (error) {
      console.error("Error saving from address:", error);
      toast.error("Failed to save from address");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Test Email Delivery</h3>
        <p className="text-sm text-muted-foreground">
          Send a test email to verify that your email delivery system is working
          correctly.
        </p>
      </div>

      <form onSubmit={handleTestEmail} className="space-y-4">
        <div className="grid gap-2">
          <Label htmlFor="test-email">Test Email Address</Label>
          <Input
            id="test-email"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="Enter email address for testing"
            required
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="template-select">Email Template</Label>
          <Select
            value={selectedTemplate}
            onValueChange={(value) =>
              setSelectedTemplate(value as EmailTemplateType)
            }
          >
            <SelectTrigger id="template-select" className="w-full">
              <SelectValue placeholder="Select a template" />
            </SelectTrigger>
            <SelectContent>
              {templateGroups.map((group) => (
                <React.Fragment key={group.label}>
                  <div className="py-2 px-2 text-sm text-muted-foreground font-semibold">
                    {group.label}
                  </div>
                  {group.templates.map((template) => (
                    <SelectItem key={template.value} value={template.value}>
                      {template.label}
                    </SelectItem>
                  ))}
                </React.Fragment>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="from-email">Platform From Address</Label>
          <div className="flex gap-2">
            <Input
              id="from-email"
              type="text"
              value={localFromAddress}
              onChange={(e) => setLocalFromAddress(e.target.value)}
              placeholder="Enter from email address"
            />
            <Button
              type="button"
              variant="outline"
              onClick={async () => {
                if (!localFromAddress) return;

                setIsSaving(true);
                try {
                  const { error } = await supabase.from("app_settings").upsert(
                    {
                      key: "email_from_address",
                      value: localFromAddress,
                    },
                    {
                      onConflict: "key",
                    }
                  );

                  if (error) throw error;

                  onFromAddressChange?.(localFromAddress);
                  toast.success("From address saved successfully");
                } catch (error) {
                  console.error("Error saving from address:", error);
                  toast.error("Failed to save from address");
                } finally {
                  setIsSaving(false);
                }
              }}
              disabled={
                isSaving ||
                !localFromAddress ||
                localFromAddress === fromAddress
              }
            >
              {isSaving ? (
                "Saving..."
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" /> Save
                </>
              )}
            </Button>
          </div>
        </div>

        <Button
          type="submit"
          disabled={isSending || !testEmail}
          className="w-full sm:w-auto"
        >
          {isSending ? "Sending..." : "Send Test Email"}
        </Button>
      </form>

      {testResult && (
        <Alert
          variant={testResult.success ? "default" : "destructive"}
          className="mt-4"
        >
          {testResult.success ? (
            <CheckCircle2 className="h-4 w-4 mr-2" />
          ) : (
            <AlertCircle className="h-4 w-4 mr-2" />
          )}
          <AlertDescription>{testResult.message}</AlertDescription>
        </Alert>
      )}

      <div className="border rounded-md p-4 mt-4 bg-gray-50">
        <h4 className="font-medium mb-2">Troubleshooting Tips</h4>
        <ul className="list-disc list-inside space-y-1 text-sm">
          <li>
            Verify that your Resend API key is configured correctly in Supabase
            secrets
          </li>
          <li>Check that your email domain is verified in Resend</li>
          <li>Look for detailed error messages in the Edge Function logs</li>
          <li>
            Ensure your account has sufficient credits/limits for sending emails
          </li>
          <li>Check spam folders if the test email isn't in your inbox</li>
          <li>
            New email logs will now show proper recipient information and email
            types
          </li>
          <li>
            Check browser console for debugging logs about template selection
          </li>
        </ul>
      </div>
    </div>
  );
};
